import { StatsDateBucketInterval } from '@/generated/enums'
import {
  formatSnakeStringWithCapitalization,
  getTruncatedText,
} from '@/utils/string_utils'
import moment from 'moment'
import { processDateLabels } from './time_bucket_processing'

export type TimeSeriesChartData<K extends string> = {
  date: Date // if representing Daily or above, should be midnight in UTC timezone
  values: {
    [key in K]: number
  }
  alert_ids?: string[]
}[]

export const getXAxisTickValues = <K extends string>(
  data: TimeSeriesChartData<K>,
  bucketInterval: StatsDateBucketInterval,
): Date[] => {
  const numBuckets = data.length
  let condition: (input: { index: number; date: Date }) => boolean = () => true
  switch (bucketInterval) {
    case StatsDateBucketInterval.HOURLY:
      // show 4-12 labels (assuming max 72 hrs)
      // preferring multiples of 3 and 6 hours
      if (numBuckets >= 24) {
        condition = ({ date }) => date.getUTCHours() % 6 === 0
      } else if (numBuckets >= 12) {
        condition = ({ date }) => date.getUTCHours() % 3 === 0
      }
      break
    case StatsDateBucketInterval.DAILY:
      // show fewer labels for 90-day range
      if (numBuckets >= 85 && numBuckets <= 95) {
        // For ~90 days, show every other Monday (every 14 days)
        condition = ({ date, index }) => date.getUTCDay() === 1 && index % 2 === 0
      } else if (numBuckets >= 21) {
        // For other large ranges, show Mondays
        condition = ({ date }) => date.getUTCDay() % 7 === 1
      } else if (numBuckets >= 9) {
        condition = ({ index }) => index % 3 === 0
      }
      break
    case StatsDateBucketInterval.WEEKLY: {
      // show 3-5 labels
      const mod = Math.round(numBuckets / 4)
      condition = ({ index }) => index % mod === 0
      break
    }
    case StatsDateBucketInterval.MONTHLY:
      // show 3-8 labels (assuming max ~24 months)
      // prefer first month of the quarter
      if (numBuckets >= 9) {
        condition = ({ date }) => date.getUTCMonth() % 3 === 0
      }
      break
    default:
      throw new Error('Invalid bucket interval: ' + bucketInterval)
  }
  const dateLabels = data
    .filter((item, index) => condition({ index, date: item.date }))
    .map((item) => item.date)

  return dateLabels
}

export const makeLegendData = <K extends string>(keys: K[], colorMap: string[]) => {
  return keys.map((key) => {
    return {
      id: key,
      label:
        getTruncatedText(formatSnakeStringWithCapitalization(key), 15) +
        (formatSnakeStringWithCapitalization(key).length > 15 ? '...' : ''),
      color: colorMap[keys.indexOf(key)],
    }
  })
}

export const makeTimeLabelMap = (
  data: TimeSeriesChartData<string> | null,
  bucketInterval: StatsDateBucketInterval,
) => {
  if (!data) return {}
  const dateLabels = data.map((item) => dateToStringKey(item.date, bucketInterval))
  const formattedDateLabels = processDateLabels(dateLabels, bucketInterval)
  return Object.fromEntries(
    dateLabels.map((date, index) => [date, formattedDateLabels[index]]),
  )
}

// provides an internal key for a date, which is unique up to the granularity of the bucket
export const dateToStringKey = (
  date: Date,
  bucketInterval: StatsDateBucketInterval,
) => {
  return moment(date).format() // ISO format
}
